import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type CSSProperties,
    type FormEvent,
    type ReactElement,
} from "react";
import { Save } from "lucide-react";
import { space } from "@tokens/index";
import { Button, SkeletonRows, Stack, Text, TextInput, useViewport } from "@primitives/index";
import { AdminSection, ErrorAlert, FormActions, MutationError } from "../admin-ui";
import { useTaskTypeWeights, useUpdateTaskTypeWeights } from "../next-ranking";
import { taskTypeLabel } from "../workflows";
import { parseWeight } from "./policy";
import { ValidationNote } from "./ValidationNote";

// Weight row: label, then one numeric input. `medium`+ places them on one
// line with the input bounded in `ch`; `compact` stacks label over input.
const weightRowWideStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(10ch, 16ch)",
    alignItems: "center",
    gap: space[3],
};

export function TaskTypeWeightsSection(): ReactElement {
    const { isCompact } = useViewport();
    const query = useTaskTypeWeights();
    const mutation = useUpdateTaskTypeWeights();
    const data = query.data;

    // Draft is keyed by task-type wire value, values held as strings so a
    // half-typed "1." doesn't fight the controlled input. Reseeded whenever
    // the server copy changes (initial load and post-save), which is also
    // how a successful save clears the dirty state.
    const [draft, setDraft] = useState<Readonly<Record<string, string>>>({});

    useEffect(() => {
        if (data === undefined) return;
        const seed: Record<string, string> = {};
        for (const type of data.knownTaskTypes) {
            const weight = data.weights[type] ?? data.neutralWeight;
            seed[type] = String(weight);
        }
        setDraft(seed);
    }, [data]);

    const anyInvalid = useMemo(
        () =>
            data !== undefined &&
            data.knownTaskTypes.some((type) => parseWeight(draft[type] ?? "") === null),
        [data, draft],
    );

    const dirty = useMemo(() => {
        if (data === undefined) return false;
        return data.knownTaskTypes.some((type) => {
            const current = data.weights[type] ?? data.neutralWeight;
            const next = parseWeight(draft[type] ?? "");
            return next !== null && next !== current;
        });
    }, [data, draft]);

    const handleSave = useCallback(() => {
        if (data === undefined) return;
        // Persist only the non-neutral rows; the server resets every omitted
        // known type to neutral, so this keeps the stored doc minimal and
        // self-cleaning.
        const weights: Record<string, number> = {};
        for (const type of data.knownTaskTypes) {
            const value = parseWeight(draft[type] ?? "");
            if (value !== null && value !== data.neutralWeight) {
                weights[type] = value;
            }
        }
        mutation.mutate({ weights });
    }, [data, draft, mutation]);

    const canSave = dirty && !anyInvalid && !mutation.isPending;

    // Enter in any weight input saves, through the form, when the draft is valid.
    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (canSave) handleSave();
        },
        [canSave, handleSave],
    );

    return (
        <AdminSection
            title="Task-type weights"
            description={`A per-task-type multiplier on the urgency score. Neutral is ${
                data !== undefined ? String(data.neutralWeight) : "1"
            }; messages and any type left at neutral score unweighted.`}
        >
            {query.isLoading && (
                <SkeletonRows rows={3} rowHeight={40} shape="card" label="Loading task-type weights" />
            )}
            {query.error !== null && (
                <ErrorAlert onRetry={() => void query.refetch()}>
                    Couldn&rsquo;t load task-type weights. {query.error.message}
                </ErrorAlert>
            )}

            {data !== undefined && (
                <Stack direction="column" gap={3}>
                    <form onSubmit={handleSubmit} style={{ display: "contents" }}>
                    {data.knownTaskTypes.map((type) => {
                        const raw = draft[type] ?? "";
                        const invalid = parseWeight(raw) === null;
                        const input = (
                            <TextInput
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.1"
                                fullWidth
                                error={invalid}
                                value={raw}
                                aria-label={`Weight for ${taskTypeLabel(type)}`}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDraft((prev) => ({ ...prev, [type]: value }));
                                }}
                            />
                        );
                        if (isCompact) {
                            return (
                                <div
                                    key={type}
                                    style={{ display: "grid", gap: space[1], minWidth: 0 }}
                                >
                                    <Text role="bodySm" weight="medium" tone="muted" truncate>
                                        {taskTypeLabel(type)}
                                    </Text>
                                    {input}
                                </div>
                            );
                        }
                        return (
                            <div key={type} style={weightRowWideStyle}>
                                <Text role="body" truncate>
                                    {taskTypeLabel(type)}
                                </Text>
                                {input}
                            </div>
                        );
                    })}

                    <MutationError error={mutation.error} />

                    <FormActions
                        note={
                            anyInvalid ? (
                                <ValidationNote>
                                    Every weight must be a number greater than 0.
                                </ValidationNote>
                            ) : undefined
                        }
                        primary={
                            <Button type="submit" variant="primary" size="md" disabled={!canSave}>
                                <Save size={14} strokeWidth={2.25} aria-hidden="true" />
                                {mutation.isPending ? "Saving…" : "Save weights"}
                            </Button>
                        }
                    />
                    </form>
                </Stack>
            )}
        </AdminSection>
    );
}
